import { useCallback, useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    FormControlLabel,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    IconButton,
    Stack,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { useNavigate } from 'react-router';
import InfoIcon from '@mui/icons-material/Info';
import { copyObject } from '../../../utils/common';
import { allWidgetsList } from '../Shared/AllWidgetsList';
import { WidgetsEnum } from '../../../utils/constants';
import { qaMetricSummary } from './MetricSummaryService';
import Authorization from '../../../utils/Authorization';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import QaMetricsView from '../../../Models/MetricSummary/QaMetricsView.model';
import { setIsWidgetExisting, setWidgetFromMetricSummary } from '../../../store/smartDashboard.slice';
import { MetricTypeEnum } from '../../../Enum/MetricTypeEnum';
import { setShowMessage } from '../../../store/shared.slice';
import '../../Widgets/Widgets.css';

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: 50 * 4.5,
            width: 250,
        },
    },
};

const QaMetricSummary = () => {
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const authData = new Authorization();
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);

    const isCEO = authData.Role === 'CEO';
    const { customTabsList } = useSelector((state: RootState) => state.smartDashboard);

    const [documentMetric, setDocumentMetric] = useState<QaMetricsView>(new QaMetricsView());
    const [teamInPersonMetric, setTeamInPersonMetric] = useState<QaMetricsView>(new QaMetricsView());
    const [individualInPersonMetric, setIndividualInPersonMetric] = useState<QaMetricsView>(new QaMetricsView());
    const [populationMetric, setPopulationMetric] = useState<QaMetricsView>(new QaMetricsView());

    const [isQaMetricShow, setIsQaMetricShow] = useState<boolean>(false);

    const [isDocumentTooltipShow, setIsDocumentTooltipShow] = useState<boolean>(false);
    const [isTeamInPersonTooltipShow, setIsTeamInPersonTooltipShow] = useState<boolean>(false);
    const [isIndividualInPersonTooltipShow, setIsIndividualInPersonTooltipShow] = useState<boolean>(false);
    const [isPopulationTooltipShow, setIsPopulationTooltipShow] = useState<boolean>(false);
    const [providerTeam, setProviderTeam] = useState('All');

    const {
        selectedProvider,
        setSelectedProvider,
        showInActiveProviders,
        setShowInActiveProviders,

        setAppointmentSelectedProvider,
        setAppointmentDateFilter,
        setAppointmentShowInActiveProviders,

        setEngagementDateFilter,
        setEngagementSelectedTeam,
        setEngagementShowInActiveTeams,

        setIndividualEngagementDateFilter,
        setIndividualEngagementSelectedProvider,
        setIndividualEngagementShowInActiveProviders,

        setTcTeam,
        setTcShowInActiveTeams,
    } = useWidgetContext();

    const {
        selectedTeam,
        showInActiveTeams,

        setAdultHealthTeam,
        setAdultHealthIncludePastOpenStatus,
        setAdultHealthShowInActiveTeam,

        setBpTeam,
        setBpIncludePastOpenStatus,
        setBpShowInActiveTeam,

        setBmiTeam,
        setBmiIncludePastOpenStatus,
        setBmiShowInActiveTeam,

        setHgba1cTeam,
        setHgba1cIncludePastOpenStatus,
        setHgba1cShowInActiveTeam,

        setDiabeticEyeExamTeam,
        setDiabeticEyeExamIncludePastOpenStatus,
        setDiabeticEyeExamShowInActiveTeam,

        setMedicationReconciliationTeam,
        setMedicationReconciliationIncludePastOpenStatus,
        setMedicationReconciliationShowInActiveTeam,

        setDepressionScreeningTeam,
        setDepressionScreeningIncludePastOpenStatus,
        setDepressionScreeningShowInActiveTeam,

        setDepressionFollowUpTeam,
        setDepressionFollowUpIncludePastOpenStatus,
        setDepressionFollowUpShowInActiveTeam,

        setBhDiagnosticTeam,
        setBhDiagnosticIncludePastOpenStatus,
        setBhDiagnosticShowInActiveTeam,

        setHgbA1cForBhDxLoadedTeam,
        setHgbA1cForBhDxIncludePastOpenStatus,
        setHgbA1cForBhDxShowInActiveTeam,

        setLipidForBhDxLoadedTeam,
        setLipidForBhDxIncludePastOpenStatus,
        setLipidForBhDxShowInActiveTeam,

        setCervicalCancerScreeningTeam,
        setCervicalCancerScreeningIncludePastOpenStatus,
        setCervicalCancerScreeningShowInActiveTeam,

        setColorectalCancerScreeningTeam,
        setColorectalCancerScreeningIncludePastOpenStatus,
        setColorectalCancerScreeningShowInActiveTeam,
    } = useHedisMetricsContext();

    const filteredProviders = (showInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];

    useEffect(() => {
        if (filteredProviders.length && !selectedProvider) {
            if (isCEO) {
                setSelectedProvider('All');
            } else {
                const loggedInProviderId = authData.UserId;
                if (loggedInProviderId) {
                    const providerInfo = filteredProviders.find((t) => t.Id === loggedInProviderId);
                    if (providerInfo) {
                        setSelectedProvider(loggedInProviderId);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authData.UserId, setSelectedProvider, activeProviders]);

    useEffect(() => {
        if (filteredProviders.length && selectedProvider) {
            setIsQaMetricShow(false);
            const changeFilter = async () => {
                const data = await qaMetricSummary(selectedProvider, showInActiveProviders);
                if (data?.length) {
                    setDocumentMetric(data.find((m) => m.MetricType === MetricTypeEnum.Documentation));
                    setTeamInPersonMetric(data.find((m) => m.MetricType === MetricTypeEnum.TeamInPersonVisits));
                    setPopulationMetric(data.find((m) => m.MetricType === MetricTypeEnum.ActiveMembers));
                    setIndividualInPersonMetric(data.find((m) => m.MetricType === MetricTypeEnum.IndividualInPersonVisits));
                }
                else {
                    const defaultMetric = new QaMetricsView();
                    setDocumentMetric(defaultMetric);
                    setTeamInPersonMetric(defaultMetric);
                    setPopulationMetric(defaultMetric);
                    setIndividualInPersonMetric(defaultMetric);
                }
                setIsQaMetricShow(true);
            };
            void changeFilter();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProvider, showInActiveProviders]);

    useEffect(() => {
        if (selectedProvider) {
            const selectedTeamInfo = teams.find((t) => t.TeamRoles.some((role) => role.AssignedProviderId === selectedProvider));
            if (selectedTeamInfo) {
                setProviderTeam(selectedTeamInfo.Id);
            }
        }
    }, [selectedProvider, teams]);

    const toggleInactiveHandler = useCallback((isChecked: boolean) => {
        setShowInActiveProviders(isChecked);
        setSelectedProvider('All');
    }, [setShowInActiveProviders, setSelectedProvider]);

    const navigateToCustomDashboard = useCallback((item: string) => {
        if (customTabsList?.length) {
            const tabList = copyObject(customTabsList);
            const homeTab = tabList[0];
            if (homeTab) {
                let component: any = null;
                let isExistingItem = true;
                switch (item) {
                    case 'Documentation':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.DOCUMENTATION_TRACKING_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Documentation Tracking Summary'));
                        break;
                    case 'Team In Person Visit Rate':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.TEAM_IN_PERSON_VISITS_RATE);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Team In Person Visit Rate'));
                        break;
                    case 'Individual Provider Visit Performance':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.INDIVIDUAL_PROVIDER_VISIT_PERFORMANCE);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Individual Provider Visit Performance'));
                        break;
                    case 'Active Members':
                        component = allWidgetsList.find((w) => w.name === WidgetsEnum.TEAM_MEMBER_SUMMARY);
                        isExistingItem = homeTab.visibleWidgets.some((m) => m === component?.name);
                        dispatch(setWidgetFromMetricSummary('Team Member Summary'));
                        break;
                }
                setAppointmentSelectedProvider(selectedProvider);
                setAppointmentDateFilter(TimePeriodFilterEnum.Last6Months);
                setAppointmentShowInActiveProviders(showInActiveProviders);

                setEngagementSelectedTeam(providerTeam);
                setEngagementDateFilter(TimePeriodFilterEnum.Last6Months);
                setEngagementShowInActiveTeams(showInActiveProviders);

                setIndividualEngagementSelectedProvider(selectedProvider);
                setIndividualEngagementDateFilter(TimePeriodFilterEnum.Last6Months);
                setIndividualEngagementShowInActiveProviders(showInActiveProviders);

                setHgba1cTeam(selectedTeam);
                setHgba1cIncludePastOpenStatus(true);
                setHgba1cShowInActiveTeam(showInActiveTeams);

                setDiabeticEyeExamTeam(selectedTeam);
                setDiabeticEyeExamIncludePastOpenStatus(true);
                setDiabeticEyeExamShowInActiveTeam(showInActiveTeams);

                setBpTeam(selectedTeam);
                setBpIncludePastOpenStatus(true);
                setBpShowInActiveTeam(showInActiveTeams);

                setBmiTeam(selectedTeam);
                setBmiIncludePastOpenStatus(true);
                setBmiShowInActiveTeam(showInActiveTeams);

                setAdultHealthTeam(selectedTeam);
                setAdultHealthIncludePastOpenStatus(true);
                setAdultHealthShowInActiveTeam(showInActiveTeams);

                setDepressionScreeningTeam(selectedTeam);
                setDepressionScreeningIncludePastOpenStatus(true);
                setDepressionScreeningShowInActiveTeam(showInActiveTeams);

                setDepressionFollowUpTeam(selectedTeam);
                setDepressionFollowUpIncludePastOpenStatus(true);
                setDepressionFollowUpShowInActiveTeam(showInActiveTeams);

                setMedicationReconciliationTeam(selectedTeam);
                setMedicationReconciliationIncludePastOpenStatus(true);
                setMedicationReconciliationShowInActiveTeam(showInActiveTeams);

                setBhDiagnosticTeam(selectedTeam);
                setBhDiagnosticIncludePastOpenStatus(true);
                setBhDiagnosticShowInActiveTeam(showInActiveTeams);

                setHgbA1cForBhDxLoadedTeam(selectedTeam);
                setHgbA1cForBhDxIncludePastOpenStatus(true);
                setHgbA1cForBhDxShowInActiveTeam(showInActiveTeams);

                setLipidForBhDxLoadedTeam(selectedTeam);
                setLipidForBhDxIncludePastOpenStatus(true);
                setLipidForBhDxShowInActiveTeam(showInActiveTeams);

                setTcTeam(selectedTeam);
                setTcShowInActiveTeams(showInActiveTeams);

                setCervicalCancerScreeningTeam(selectedTeam);
                setCervicalCancerScreeningIncludePastOpenStatus(true);
                setCervicalCancerScreeningShowInActiveTeam(showInActiveTeams);

                setColorectalCancerScreeningTeam(selectedTeam);
                setColorectalCancerScreeningIncludePastOpenStatus(true);
                setColorectalCancerScreeningShowInActiveTeam(showInActiveTeams);

                dispatch(setIsWidgetExisting(isExistingItem));
                history(`/provider/metricsummary/smart-dashboard`);
            }
        } else {
            dispatch(setShowMessage(true, 'Please add Home tab first', 'error'));
        }
    }, [customTabsList, selectedProvider, showInActiveProviders, selectedTeam, showInActiveTeams, providerTeam, dispatch, history, setAppointmentSelectedProvider, setAppointmentDateFilter, setAppointmentShowInActiveProviders, setEngagementSelectedTeam, setEngagementDateFilter, setEngagementShowInActiveTeams, setIndividualEngagementSelectedProvider, setIndividualEngagementDateFilter, setIndividualEngagementShowInActiveProviders, setHgba1cTeam, setHgba1cIncludePastOpenStatus, setHgba1cShowInActiveTeam, setDiabeticEyeExamTeam, setDiabeticEyeExamIncludePastOpenStatus, setDiabeticEyeExamShowInActiveTeam, setBpTeam, setBpIncludePastOpenStatus, setBpShowInActiveTeam, setBmiTeam, setBmiIncludePastOpenStatus, setBmiShowInActiveTeam, setAdultHealthTeam, setAdultHealthIncludePastOpenStatus, setAdultHealthShowInActiveTeam, setDepressionScreeningTeam, setDepressionScreeningIncludePastOpenStatus, setDepressionScreeningShowInActiveTeam, setDepressionFollowUpTeam, setDepressionFollowUpIncludePastOpenStatus, setDepressionFollowUpShowInActiveTeam, setMedicationReconciliationTeam, setMedicationReconciliationIncludePastOpenStatus, setMedicationReconciliationShowInActiveTeam, setBhDiagnosticTeam, setBhDiagnosticIncludePastOpenStatus, setBhDiagnosticShowInActiveTeam, setHgbA1cForBhDxLoadedTeam, setHgbA1cForBhDxIncludePastOpenStatus, setHgbA1cForBhDxShowInActiveTeam, setLipidForBhDxLoadedTeam, setLipidForBhDxIncludePastOpenStatus, setLipidForBhDxShowInActiveTeam, setTcTeam, setTcShowInActiveTeams]);

    const handleDocumentClick = useCallback(() => {
        setIsDocumentTooltipShow((prev) => !prev);
    }, []);

    const handleTeamInPersonClick = useCallback(() => {
        setIsTeamInPersonTooltipShow((prev) => !prev);
    }, []);

    const handleIndividualInPersonClick = useCallback(() => {
        setIsIndividualInPersonTooltipShow((prev) => !prev);
    }, []);

    const handlePopulationClick = useCallback(() => {
        setIsPopulationTooltipShow((prev) => !prev);
    }, []);

    return (
        <>
            <Paper className="smartdashboard-widget" elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display="inline-block" variant="h6" color="primary" className="pointer">
                            QA Metric Summary
                        </Typography>
                    </Grid>
                    {isCEO && (
                        <Grid size={3}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Provider</InputLabel>
                                <Select
                                    value={selectedProvider}
                                    label="Provider"
                                    name="Provider"
                                    onChange={(e) => setSelectedProvider(e.target.value)}
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={<Checkbox checked={showInActiveProviders} onChange={(e) => toggleInactiveHandler(e.target.checked)} />}
                                        label="Show Inactive"
                                    />
                                    {filteredProviders.map((provider) => (
                                        <MenuItem
                                            key={provider.Id}
                                            value={provider.Id}
                                            sx={{ color: provider.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}
                                        >
                                            {`${provider.LastName}, ${provider.FirstName}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} marginTop={0.5} minHeight="56vh" sx={{ position: 'relative' }}>
                    {!isQaMetricShow && (
                        <Box className="loader-center">
                            <CircularProgress />
                        </Box>
                    )}
                    {isQaMetricShow && (
                        <>
                            <Grid size={4} textAlign="center" paddingLeft="30px">
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={0.5}
                                    sx={{ width: '100%' }}
                                >
                                    <Tooltip title="Documentation">
                                        <Typography
                                            fontWeight="bold"
                                            variant="body1"
                                            sx={{
                                                minWidth: 40,
                                                maxWidth: '100%',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Documentation
                                        </Typography>
                                    </Tooltip>
                                    <Tooltip
                                        open={isDocumentTooltipShow}
                                        title={
                                            <div>
                                                <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                    Documented :
                                                </Typography>
                                                <Typography variant="caption">
                                                    Represents the percentage of appointments that have been documented within three business days of the
                                                    appointment date
                                                </Typography>
                                            </div>
                                        }
                                        PopperProps={{ style: { zIndex: 1000 } }}
                                    >
                                        <IconButton onClick={handleDocumentClick}>
                                            <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Card
                                    sx={{
                                        ':hover': {
                                            cursor: 'pointer',
                                            boxShadow: 10,
                                            border: 0,
                                        },
                                        boxShadow: 0,
                                    }}
                                    variant="outlined"
                                    onClick={() => navigateToCustomDashboard('Documentation')}
                                >
                                    <CardContent
                                        sx={{
                                            backgroundColor: documentMetric?.IsMet ? '#82d782' : '#FD5F61',
                                            color: documentMetric?.IsMet ? 'black' : 'white',
                                            paddingBottom: '5px !important',
                                        }}
                                    >
                                        <Stack spacing={1} alignItems={'center'} justifyContent={'center'}>
                                            <Typography fontWeight="bold" variant="h4">
                                                {documentMetric?.Percentage ?? 0}%
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} justifyContent="center" paddingTop={0.8}>
                                            <Typography fontWeight="bold" variant="body2">
                                                {documentMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                            <Typography fontWeight="bold" variant="body2">
                                                (Goal: {documentMetric?.Goal ?? 0}%)
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} justifyContent="center" paddingTop={0.9}>
                                            <Typography fontWeight="bold" variant="subtitle2">
                                                Average Days: {documentMetric?.AverageDays ?? 0}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={4} textAlign="center" paddingLeft="30px">
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={0.5}
                                    sx={{ width: '100%' }}
                                >
                                    <Tooltip title="Team In Person Visit Rate">
                                        <Typography
                                            fontWeight="bold"
                                            variant="body1"
                                            sx={{
                                                minWidth: 40,
                                                maxWidth: '100%',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Team In Person Visit Rate
                                        </Typography>
                                    </Tooltip>
                                    <Tooltip
                                        open={isTeamInPersonTooltipShow}
                                        title={
                                            <div>
                                                <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                    Visited :
                                                </Typography>
                                                <Typography variant="caption">
                                                    Averages selected team&apos;s rate of completed in-person visits (Contacted = Yes, Means of Engagement = In
                                                    Person) for any reason for engagement per active member over the selected months. Members will only be counted
                                                    for months that they were active
                                                </Typography>
                                            </div>
                                        }
                                        PopperProps={{ style: { zIndex: 1000 } }}
                                    >
                                        <IconButton onClick={handleTeamInPersonClick}>
                                            <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Card
                                    sx={{
                                        ':hover': {
                                            cursor: 'pointer',
                                            boxShadow: 10,
                                            border: 0,
                                        },
                                        boxShadow: 0,
                                    }}
                                    variant="outlined"
                                    onClick={() => navigateToCustomDashboard('Team In Person Visit Rate')}
                                >
                                    <CardContent
                                        sx={{
                                            backgroundColor: teamInPersonMetric?.IsMet ? '#82d782' : '#FD5F61',
                                            color: teamInPersonMetric?.IsMet ? 'black' : 'white',
                                        }}
                                    >
                                        <Stack spacing={2} alignItems="center" justifyContent="center">
                                            <Typography fontWeight="bold" variant="h4">
                                                {teamInPersonMetric?.Percentage ?? 0}%
                                            </Typography>
                                            <Stack direction="row" spacing={2}>
                                                <Typography fontWeight="bold" variant="body2">
                                                    {teamInPersonMetric?.IsMet ? 'Met' : 'Not Met'}
                                                </Typography>
                                                <Typography fontWeight="bold" variant="body2">
                                                    (Goal: {teamInPersonMetric?.Goal ?? 0}%)
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={4} textAlign="center" paddingLeft="30px">
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={0.5}
                                    sx={{ width: '100%' }}
                                >
                                    <Tooltip title="Population Size">
                                        <Typography
                                            fontWeight="bold"
                                            variant="body1"
                                            sx={{
                                                minWidth: 40,
                                                maxWidth: '100%',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Population Size
                                        </Typography>
                                    </Tooltip>
                                    <Tooltip
                                        open={isPopulationTooltipShow}
                                        title={
                                            <div>
                                                <Typography variant="caption">Number of members in active status</Typography>
                                            </div>
                                        }
                                        PopperProps={{ style: { zIndex: 1000 } }}
                                    >
                                        <IconButton onClick={handlePopulationClick}>
                                            <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Card
                                    sx={{
                                        ':hover': {
                                            cursor: 'pointer',
                                            boxShadow: 10,
                                            border: 0,
                                        },
                                        boxShadow: 0,
                                    }}
                                    variant="outlined"
                                    onClick={() => navigateToCustomDashboard('Active Members')}
                                >
                                    <CardContent
                                        sx={{
                                            backgroundColor: populationMetric?.IsMet ? '#82d782' : '#FD5F61',
                                            color: populationMetric?.IsMet ? 'black' : 'white',
                                            paddingBottom: '5px !important',
                                        }}
                                    >
                                        <Stack spacing={1} alignItems="center" justifyContent="center">
                                            <Typography fontWeight="bold" variant="h4">
                                                {populationMetric?.Percentage ?? 0}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} justifyContent="center" paddingTop={0.8}>
                                            <Typography fontWeight="bold" variant="body2">
                                                {populationMetric?.IsMet ? 'Met' : 'Not Met'}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} justifyContent="center" paddingTop={0.9}>
                                            <Typography fontWeight="bold" variant="subtitle2">
                                                (Active Members: {populationMetric?.Goal ?? 0})
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={4} textAlign="center" paddingLeft="30px">
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={0.5}
                                    sx={{ width: '100%' }}
                                >
                                    <Tooltip title="Individual Provider Visit Performance">
                                        <Typography
                                            fontWeight="bold"
                                            variant="body1"
                                            sx={{
                                                minWidth: 40,
                                                maxWidth: '100%',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Individual Provider Visit Performance
                                        </Typography>
                                    </Tooltip>
                                    <Tooltip
                                        open={isIndividualInPersonTooltipShow}
                                        title={
                                            <div>
                                                <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                    Visited :
                                                </Typography>
                                                <Typography variant="caption">
                                                    Averages selected provider&apos;s rate of completed in-person visits (Contacted = Yes, Means of Engagement =
                                                    In Person) for any reason for engagement.
                                                </Typography>
                                            </div>
                                        }
                                        PopperProps={{ style: { zIndex: 1000 } }}
                                    >
                                        <IconButton onClick={handleIndividualInPersonClick}>
                                            <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Card
                                    sx={{
                                        ':hover': {
                                            cursor: 'pointer',
                                            boxShadow: 10,
                                            border: 0,
                                        },
                                        boxShadow: 0,
                                    }}
                                    variant="outlined"
                                    onClick={() => navigateToCustomDashboard('Individual Provider Visit Performance')}
                                >
                                    <CardContent
                                        sx={{
                                            backgroundColor: individualInPersonMetric?.IsMet ? '#82d782' : '#FD5F61',
                                            color: individualInPersonMetric?.IsMet ? 'black' : 'white',
                                        }}
                                    >
                                        <Stack spacing={2} alignItems="center" justifyContent="center">
                                            <Typography fontWeight="bold" variant="h4">
                                                {individualInPersonMetric?.Percentage ?? 0}%
                                            </Typography>
                                            <Stack direction="row" spacing={2}>
                                                <Typography fontWeight="bold" variant="body2">
                                                    {individualInPersonMetric?.IsMet ? 'Met' : 'Not Met'}
                                                </Typography>
                                                <Typography fontWeight="bold" variant="body2">
                                                    (Goal: {individualInPersonMetric?.Goal ?? 0}%)
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            {/* To fix height problem */}
                            <Grid size={12} textAlign="center" paddingLeft="30px">
                                <Card sx={{ minHeight: '150px', display: 'inline-block' }} />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>
        </>
    );
};

export default QaMetricSummary;
